import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import emDash from '~/components/emdash'

import type { ChargerEventModel } from '../api/client'
import type {
  BulkCommissionActionRequestModel,
  ChargerModel,
  ConnectorModel,
  ServiceEventModel
} from '../api/client/chargers'

export type StartBehavior = Exclude<ChargerEventModel['startBehavior'], undefined>
export type StopBehavior = Exclude<ChargerEventModel['stopBehavior'], undefined>

export function useStartBehaviorFormatter() {
  const t = useTranslations('data.chargers')
  return useCallback((startBehavior?: StartBehavior) => (startBehavior ? t(`metadata_startBehavior_options_${startBehavior}`) : emDash), [t])
}

export function useStopBehaviorFormatter() {
  const t = useTranslations('data.chargers')
  return useCallback((stopBehavior?: StopBehavior) => (stopBehavior ? t(`metadata_stopBehavior_options_${stopBehavior}`) : emDash), [t])
}

export type CommissioningState = Required<ServiceEventModel>['commissioningState']
export type CommissioningStateAction = BulkCommissionActionRequestModel['commissioningState']

export type ConnectorType = NonNullable<ConnectorModel['connectorType']>
const connectorTypes = new Set<ConnectorType>(['J1772', 'Type2', 'CHAdeMO', 'CCS1', 'CCS2', 'NACS', 'MCS', 'Other'])

export function isConnectorType(connectorType: string): connectorType is ConnectorType {
  return connectorTypes.has(connectorType as ConnectorType)
}

export type ConnectorFormat = NonNullable<ConnectorModel['connectorFormat']>
const connectorFormats = new Set<ConnectorFormat>(['SOCKET', 'CABLE'])

export function isConnectorFormat(connectorFormat: string): connectorFormat is ConnectorFormat {
  return connectorFormats.has(connectorFormat as ConnectorFormat)
}

export type ConnectorPowerType = NonNullable<ConnectorModel['connectorPowerType']>
const connectorPowerTypes = new Set<ConnectorPowerType>([
  'AC_1_PHASE',
  'AC_2_PHASE',
  'AC_2_PHASE_SPLIT',
  'AC_3_PHASE',
  'DC'
])

export function isConnectorPowerType(connectorPowerType: string): connectorPowerType is ConnectorPowerType {
  return connectorPowerTypes.has(connectorPowerType as ConnectorPowerType)
}

export type FirmwareStatus = NonNullable<ChargerModel['firmwareUpdateInferredStatus']>
